import { useState, useCallback, useRef } from 'react';

interface UseUnsavedChangesOptions {
  isDirty: boolean;
  onSave?: () => Promise<void> | void;
  message?: string;
  showSaveOption?: boolean;
}

interface UseUnsavedChangesReturn {
  showModal: boolean;
  pendingNavigation: (() => void) | null;
  checkUnsavedChanges: (navigationFn: () => void) => void;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  saveAndContinue: () => Promise<void>;
}

/**
 * Custom hook for managing unsaved changes with a modal dialog
 * Replaces browser's default beforeunload behavior with a custom modal
 */
export const useUnsavedChanges = ({
  isDirty,
  onSave,
  message,
  showSaveOption = false
}: UseUnsavedChangesOptions): UseUnsavedChangesReturn => {
  const [showModal, setShowModal] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  /**
   * Check if there are unsaved changes before navigation
   * If there are changes, show modal; otherwise proceed with navigation
   */
  const checkUnsavedChanges = useCallback((navigationFn: () => void) => {
    if (isDirty) {
      pendingNavigationRef.current = navigationFn;
      setShowModal(true);
    } else {
      navigationFn();
    }
  }, [isDirty]);

  /**
   * Confirm navigation and discard changes
   */
  const confirmNavigation = useCallback(() => {
    setShowModal(false);
    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    }
  }, []);

  /**
   * Cancel navigation and stay on current page
   */
  const cancelNavigation = useCallback(() => {
    setShowModal(false);
    pendingNavigationRef.current = null;
  }, []);

  /**
   * Save changes and then continue with navigation
   */
  const saveAndContinue = useCallback(async () => {
    if (onSave) {
      try {
        await onSave();
        setShowModal(false);
        if (pendingNavigationRef.current) {
          pendingNavigationRef.current();
          pendingNavigationRef.current = null;
        }
      } catch (error) {
        console.error('Failed to save changes:', error);
        // Keep modal open if save fails
      }
    }
  }, [onSave]);

  return {
    showModal,
    pendingNavigation: pendingNavigationRef.current,
    checkUnsavedChanges,
    confirmNavigation,
    cancelNavigation,
    saveAndContinue
  };
};
